import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import {
  AlertOctagon, TrendingUp, Wrench, Bike, ShieldAlert, IndianRupee, Activity, RefreshCw, Radio, MessageSquareWarning,
} from "lucide-react";

const CATEGORY_TONE = {
  ops:        "text-blue-700 dark:text-blue-300 bg-blue-500/10",
  approval:   "text-orange-700 dark:text-orange-300 bg-orange-500/10",
  financial:  "text-emerald-700 dark:text-emerald-300 bg-emerald-500/10",
  milestone:  "text-slate-700 dark:text-slate-300 bg-slate-500/10",
};

const PRIORITY_TONE = {
  High:     "bg-orange-100 text-orange-700 border-orange-300 dark:bg-orange-500/15 dark:text-orange-300",
  Critical: "bg-rose-100 text-rose-700 border-rose-300 dark:bg-rose-500/15 dark:text-rose-300",
};

export default function WarRoom() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tick, setTick] = useState(Date.now());

  const load = async () => {
    try {
      const r = await api.get("/admin/war-room/summary");
      setData(r.data);
      setError("");
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load war room");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); const t = setInterval(() => { load(); setTick(Date.now()); }, 30000); return () => clearInterval(t); }, []);

  if (loading) return <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center text-slate-500">Loading war room…</div>;
  if (error || !data) return <div className="p-8 text-rose-600">{error || "No data"}</div>;

  const { stuck, high_value_pending, workshop_backlog, delayed_surveyors, sla_risk, revenue, pipeline, urgent_complaints } = data;

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-slate-950 text-white py-8" data-testid="war-room-page">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.3em] text-orange-400 mb-1 flex items-center gap-2">
              <Radio className="w-3 h-3 animate-pulse" /> Mission Control · Live
            </div>
            <h1 className="font-heading font-black text-3xl sm:text-4xl">CEO War Room</h1>
          </div>
          <button onClick={load} className="text-xs text-slate-400 hover:text-white flex items-center gap-1.5 border border-slate-800 rounded-lg px-3 py-1.5" data-testid="wr-refresh">
            <RefreshCw className="w-3 h-3" /> Refreshed {new Date(tick).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
          </button>
        </div>

        {/* Revenue row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <RevenueCard testid="wr-settled-today" label="Settled today (₹)" value={`₹${(revenue.settled_today_inr || 0).toLocaleString("en-IN")}`} sub={`${revenue.settlements_today} settlements`} />
          <RevenueCard testid="wr-cash-today"    label="Cash collected today" value={`₹${(revenue.cash_collected_today_inr || 0).toLocaleString("en-IN")}`} sub="Same-day proxy" />
          <RevenueCard testid="wr-settled-month" label="Settled MTD (₹)"      value={`₹${(revenue.settlements_month_inr || 0).toLocaleString("en-IN")}`} sub="Month-to-date" />
          <RevenueCard testid="wr-sla-total"     label="Claims at SLA risk"   value={`${sla_risk.length}`} sub="over SLA now" tone="rose" />
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* STUCK CLAIMS */}
          <Panel testid="wr-stuck" title="Claims Stuck" icon={AlertOctagon} tone="rose">
            <StuckRow label="Over 24h"  count={stuck.totals["24h"]} items={stuck.over_24h} tone="text-amber-400" />
            <StuckRow label="Over 48h"  count={stuck.totals["48h"]} items={stuck.over_48h} tone="text-orange-400" />
            <StuckRow label="Over 72h"  count={stuck.totals["72h"]} items={stuck.over_72h} tone="text-rose-400" />
          </Panel>

          {/* HIGH-VALUE PENDING APPROVAL */}
          <Panel testid="wr-hv" title="High-value awaiting approval (≥₹1L)" icon={IndianRupee} tone="orange">
            {high_value_pending.length === 0
              ? <EmptyRow text="No high-value claims pending approval." />
              : high_value_pending.map((c) => (
                <ClaimRow key={c.claim_id} claim_id={c.claim_id}
                  primary={`₹${c.value_inr.toLocaleString("en-IN")}`}
                  secondary={`${c.customer || "—"} · ${c.insurer || "—"}`}
                  tag={c.status} extra={`${c.waiting_hours}h waiting`} />
              ))}
          </Panel>

          {/* URGENT COMPLAINTS */}
          <Panel testid="wr-urgent" title="Urgent complaints" icon={MessageSquareWarning} tone="fuchsia">
            {urgent_complaints.length === 0
              ? <EmptyRow text="No urgent escalations. Breathe." />
              : urgent_complaints.map((e) => (
                <div key={e.escalation_id} className="py-2 border-b border-slate-800 last:border-0" data-testid={`wr-complaint-${e.escalation_id}`}>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="min-w-0">
                      <div className="font-mono text-xs text-slate-400">{e.claim_id} · {e.customer || "—"}</div>
                      <div className="text-sm text-slate-100 truncate">{e.reason}</div>
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase border ${PRIORITY_TONE[e.priority] || ""}`}>{e.priority}</span>
                  </div>
                </div>
              ))}
          </Panel>

          {/* WORKSHOP BACKLOG */}
          <Panel testid="wr-workshops" title="Workshops with backlog" icon={Wrench} tone="blue">
            {workshop_backlog.length === 0
              ? <EmptyRow text="No workshop backlog." />
              : workshop_backlog.map((w) => (
                <div key={w.workshop} className="py-2 border-b border-slate-800 last:border-0 flex items-center justify-between gap-3" data-testid={`wr-workshop-${w.workshop}`}>
                  <div className="min-w-0">
                    <div className="text-sm text-slate-100 truncate">{w.workshop}</div>
                    <div className="text-[10px] text-slate-500">{w.workshop_id || "—"}</div>
                  </div>
                  <div className="text-2xl font-heading font-black text-orange-400">{w.pending}</div>
                </div>
              ))}
          </Panel>

          {/* DELAYED SURVEYORS */}
          <Panel testid="wr-surveyors" title="Surveyors — reports delayed" icon={Bike} tone="amber">
            {delayed_surveyors.length === 0
              ? <EmptyRow text="All surveyors on schedule." />
              : delayed_surveyors.map((s, i) => (
                <div key={`${s.surveyor_id}-${i}`} className="py-2 border-b border-slate-800 last:border-0 flex items-center justify-between" data-testid={`wr-surveyor-${s.surveyor_id}`}>
                  <div className="min-w-0">
                    <div className="text-sm text-slate-100 truncate">{s.surveyor}</div>
                    <div className="text-[10px] font-mono text-slate-500">{s.claim_id}</div>
                  </div>
                  <div className="text-sm font-bold text-rose-400">{s.delay_hours}h late</div>
                </div>
              ))}
          </Panel>

          {/* SLA BREACH RISK */}
          <Panel testid="wr-sla" title="SLA breach risk" icon={ShieldAlert} tone="rose">
            {sla_risk.length === 0
              ? <EmptyRow text="Every claim within SLA." />
              : sla_risk.slice(0, 8).map((r) => (
                <Link to={`/claim/${r.claim_id}`} key={r.claim_id} className="block py-2 border-b border-slate-800 last:border-0 hover:bg-slate-800/50 px-1 -mx-1 rounded transition-colors" data-testid={`wr-sla-${r.claim_id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-mono text-xs text-slate-400">{r.claim_id}</div>
                      <div className="text-sm text-slate-100 truncate">{r.status}</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-sm font-bold text-rose-400">+{r.over_by_hours}h</div>
                      <div className="text-[10px] text-slate-500">SLA {r.sla_hours}h</div>
                    </div>
                  </div>
                </Link>
              ))}
          </Panel>
        </div>

        {/* LIVE PIPELINE — full width */}
        <div className="mt-6 rounded-2xl bg-slate-900 border border-slate-800 p-6" data-testid="wr-pipeline">
          <div className="flex items-center gap-2 mb-4">
            <Activity className="w-4 h-4 text-orange-400" />
            <h2 className="font-heading font-bold">Live pipeline by stage</h2>
            <span className="text-[10px] uppercase tracking-widest text-slate-500 ml-auto">24-stage workflow</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-2">
            {pipeline.map((p) => (
              <motion.div key={p.stage} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="rounded-lg bg-slate-800/40 border border-slate-800 p-3" data-testid={`wr-stage-${p.stage.replace(/\s+/g, "-").toLowerCase()}`}>
                <div className={`text-[9px] font-bold uppercase tracking-widest ${CATEGORY_TONE[p.kind] || ""} inline-block px-1.5 py-0.5 rounded mb-1.5`}>{p.kind}</div>
                <div className="font-heading font-black text-2xl">{p.count}</div>
                <div className="text-[10px] text-slate-400 leading-tight mt-0.5 line-clamp-2">{p.stage}</div>
                <div className="text-[9px] text-slate-500 mt-1">SLA {p.sla_hours}h</div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function RevenueCard({ label, value, sub, tone, testid }) {
  const ring = tone === "rose" ? "ring-rose-500/30" : "ring-emerald-500/20";
  return (
    <div className={`rounded-2xl bg-slate-900 border border-slate-800 p-5 ring-1 ${ring}`} data-testid={testid}>
      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{label}</div>
      <div className="font-heading font-black text-3xl mt-1">{value}</div>
      <div className="text-xs text-slate-500 mt-0.5">{sub}</div>
    </div>
  );
}

function Panel({ title, icon: Icon, tone = "blue", children, testid }) {
  const tones = { rose: "text-rose-400", orange: "text-orange-400", blue: "text-blue-400", amber: "text-amber-400", fuchsia: "text-fuchsia-400" };
  return (
    <div className="rounded-2xl bg-slate-900 border border-slate-800 p-5" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className={`w-4 h-4 ${tones[tone]}`} />
        <h2 className="font-heading font-bold">{title}</h2>
      </div>
      <div className="max-h-80 overflow-y-auto">{children}</div>
    </div>
  );
}

function StuckRow({ label, count, items, tone }) {
  return (
    <div className="py-2 border-b border-slate-800 last:border-0">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-semibold text-slate-300">{label}</span>
        <span className={`font-heading font-black text-2xl ${tone}`}>{count}</span>
      </div>
      {count > 0 && (
        <div className="text-[10px] text-slate-500 space-y-0.5 max-h-24 overflow-y-auto">
          {items.slice(0, 5).map((i) => (
            <Link key={i.claim_id} to={`/claim/${i.claim_id}`} className="block hover:text-slate-300 font-mono truncate">
              {i.claim_id} · {i.status} · {i.hours_stuck}h
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function ClaimRow({ claim_id, primary, secondary, tag, extra }) {
  return (
    <Link to={`/claim/${claim_id}`} className="block py-2 border-b border-slate-800 last:border-0 hover:bg-slate-800/50 px-1 -mx-1 rounded transition-colors" data-testid={`wr-hv-${claim_id}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="font-heading font-black text-lg">{primary}</div>
          <div className="text-xs text-slate-400 truncate">{secondary}</div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">{tag}</div>
          <div className="text-[10px] text-slate-400">{extra}</div>
        </div>
      </div>
    </Link>
  );
}

function EmptyRow({ text }) {
  return <div className="text-xs text-slate-500 py-6 text-center">{text}</div>;
}
